import styles from './WebshopRequestController.module.scss';

import { gql, useApolloClient } from '@apollo/client';
import { Button, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogActions, DialogContent } from '@work4all/components';
import { useActiveInterval } from '@work4all/components/lib/hooks/use-active-interval';

import { useDataProvider } from '@work4all/data';
import { remToPx } from '@work4all/data/lib/hooks/useRemToPx';

import { WebShopRequest } from '@work4all/models/lib/Classes/WebShopRequest.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { formatAsFloat } from '@work4all/utils';

import { useShadowBzObjectApiContext } from '../../../../../hooks/use-bz-shadow-object-api/use-shadow-bz-object-api-context';

export interface WebShopRequestResponseArticle {
  ArticleGroup: string;
  Curreny: string;
  Description: string;
  ExtCategoriyId: string;
  ExtProductId: string;
  Leadtime: string;
  LongDescription: string;
  Price: number;
  PriceUnit: number;
  Quantity: number;
  SchemaType: string;
  SupplierArticleNumber: string;
  Unit: string;
}

export interface WebShopRequestResponse {
  Articles: Record<string, WebShopRequestResponseArticle>;
}

const refreshInterval = 2000;

export const WebShopRequestController = (props: {
  skip: boolean;
  onResultCompleted: () => void;
}) => {
  const { skip, onResultCompleted } = props;
  const [dialogOpen, setDialogOpen] = useState(false);

  const client = useApolloClient();

  const shadowBzObjectApi = useShadowBzObjectApiContext();
  const shadowBzObjectId = shadowBzObjectApi.initialShadowObject?.id;

  const { t } = useTranslation();

  const req = useMemo<DataRequest>(() => {
    return {
      data: {
        shadowBzObjectId: null,
        response: null,
        bzObjMemberCode: null,
        bzObjType: null,
        id: null,
        insertTime: null,
        updateTime: null,
        userCode: null,
      } as WebShopRequest,
      entity: Entities.webShopRequest,
      completeDataResponse: true,
      vars: {
        shadowBzObjectId,
      },
    };
  }, [shadowBzObjectId]);

  const result = useDataProvider<WebShopRequest>(req, !shadowBzObjectId);

  const refetch = useCallback(() => {
    if (!skip) {
      result.refetch();
    }
  }, [result, skip]);

  useActiveInterval(refetch, refreshInterval);

  useEffect(() => {
    if (
      !Array.isArray(result.data) &&
      (result.data as WebShopRequest)?.response
    ) {
      setDialogOpen(true);
    } else {
      setDialogOpen(false);
    }
  }, [result.data]);

  const response = useMemo(() => {
    const data = result.data as WebShopRequest;
    return data?.response
      ? (JSON.parse(data.response) as WebShopRequestResponse)
      : null;
  }, [result.data]);

  const deleteRequest = useCallback(async () => {
    await client.query({
      fetchPolicy: 'no-cache',
      query: DELETE_OCI_WEBSHOP_REQUEST,
      variables: {
        shadowBzObjectId,
      },
    });
    setDialogOpen(false);
  }, [client, shadowBzObjectId]);

  const resolveRequest = useCallback(async () => {
    await shadowBzObjectApi.resolveWebShopRequest({
      onComplete: () => {
        deleteRequest();
        onResultCompleted();
      },
    });
  }, [deleteRequest, onResultCompleted, shadowBzObjectApi]);

  const handleConfirm = useCallback(async () => {
    if (!response?.Articles) {
      return;
    }
    resolveRequest();
    setDialogOpen(false);
  }, [resolveRequest, response?.Articles]);

  return (
    <Dialog
      open={dialogOpen}
      title={t('OCI.SUCCESS.TITLE')}
      classes={{ dialog: { paper: styles.dialog } }}
      onClose={() => setDialogOpen(false)}
    >
      <DialogContent>
        <table cellPadding={remToPx(0.25)}>
          {response?.Articles &&
            Object.keys(response?.Articles)?.map((key) => {
              const article = response.Articles[key];
              const singlePrice = article.Price / article.PriceUnit;
              const totalPrice = singlePrice * article.Quantity;
              return (
                <tr>
                  <td style={{ textAlign: 'right' }}>
                    <Typography color="var(--text03)">
                      {article.Quantity}
                    </Typography>
                  </td>
                  <td>
                    <Typography color="var(--text03)">
                      {article.Unit}
                    </Typography>
                  </td>
                  <td>
                    <Typography>{article.Description}</Typography>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <Typography color="var(--text03)">
                      {formatAsFloat(totalPrice, {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                      })}
                    </Typography>
                  </td>
                  <td>
                    <Typography color="var(--text03)">
                      {article.Curreny}
                    </Typography>
                  </td>
                </tr>
              );
            })}
        </table>
      </DialogContent>
      <DialogActions>
        <Button onClick={deleteRequest}>{t('ALERTS.DISCARD')}</Button>
        <Button onClick={handleConfirm}>{t('COMMON.APPLY')}</Button>
      </DialogActions>
    </Dialog>
  );
};

const DELETE_OCI_WEBSHOP_REQUEST = gql`
  mutation deleteWebshopRequest($shadowBzObjectId: Guid!) {
    deleteWebshopRequest(shadowBzObjectId: $shadowBzObjectId)
  }
`;
